import { useState } from 'react';

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function ChevronIcon({ open }) {
  return (
    <svg
      className={`w-4 h-4 text-[#808080] transition-transform ${open ? 'rotate-180' : ''}`}
      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

const GRANT_TYPES = [
  { id: 'NIH', label: 'NIH R01 Style', shortLabel: 'NIH', description: 'US National Institutes of Health R01 application format' },
  { id: 'Wellcome', label: 'Wellcome Trust', shortLabel: 'Wellcome', description: 'Wellcome Trust grant application style' },
  { id: 'EU_Horizon', label: 'EU Horizon', shortLabel: 'EU Horizon', description: 'European Research Council / Horizon Europe format' },
  { id: 'generic', label: 'General Grant', shortLabel: 'General', description: 'Generic grant application structure' },
];

function CollapsibleSection({ section, index }) {
  const [open, setOpen] = useState(index === 0);

  return (
    <div className="rounded-lg overflow-hidden" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-[#fafafa] hover:bg-[#f0f0f0] transition-colors text-left"
      >
        <span className="text-sm font-medium text-[#171717]">{section.heading || section.title}</span>
        <ChevronIcon open={open} />
      </button>
      {open && (
        <div className="px-4 py-3 bg-white" style={{ borderTop: '1px solid #ebebeb' }}>
          <p className="text-sm text-[#4d4d4d] leading-relaxed whitespace-pre-line">
            {section.body || section.content}
          </p>
        </div>
      )}
    </div>
  );
}

export default function GrantAssistant({ protocol = null, apiCall }) {
  const [selectedGrantType, setSelectedGrantType] = useState('NIH');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const selectedGrant = GRANT_TYPES.find((g) => g.id === selectedGrantType);

  const handleReframe = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const response = await apiCall(selectedGrantType);
      setResult(response.data || response);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join(', ')
        : typeof detail === 'string'
        ? detail
        : 'Failed to reframe protocol. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAll = async () => {
    if (!result?.sections) return;
    const fullText = result.sections
      .map((s) => `## ${s.title}\n\n${s.content}`)
      .join('\n\n---\n\n');
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = fullText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!protocol) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-[#171717]" style={{ letterSpacing: '-1.28px' }}>Grant Assistant</h2>
          <p className="text-sm text-[#4d4d4d] mt-1">
            Reframe your research protocol for specific grant bodies and funding schemes.
          </p>
        </div>
        <div
          className="bg-white rounded-lg p-8 flex flex-col items-center text-center gap-3"
          style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
        >
          <svg className="w-10 h-10 text-[#ebebeb]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          <div>
            <p className="text-[#171717] font-semibold text-sm">No Protocol Available</p>
            <p className="text-[#808080] text-sm mt-1 max-w-sm">
              Generate a Research Protocol first, then use Grant Assistant to reframe it for your target funding body.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-[#171717]" style={{ letterSpacing: '-1.28px' }}>Grant Assistant</h2>
        <p className="text-sm text-[#4d4d4d] mt-1">
          Reframe your research protocol for specific grant bodies and funding schemes.
        </p>
      </div>

      {/* Current protocol indicator */}
      <div
        className="bg-[#fafafa] rounded-lg px-4 py-3 flex items-center gap-3"
        style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
      >
        <svg className="w-4 h-4 text-[#808080] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <div className="min-w-0">
          <p className="text-xs text-[#808080] font-medium uppercase tracking-wide">Active Protocol</p>
          <p className="text-sm font-semibold text-[#171717] truncate" style={{ letterSpacing: '-0.32px' }}>
            {protocol.title || 'Untitled Protocol'}
          </p>
        </div>
      </div>

      {/* Grant type selector */}
      <div className="bg-white rounded-lg p-5 space-y-4" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
        <p className="text-sm font-medium text-[#171717]" style={{ letterSpacing: '-0.32px' }}>Select Grant Type</p>

        <div className="grid grid-cols-2 gap-3">
          {GRANT_TYPES.map((grant) => (
            <button
              key={grant.id}
              onClick={() => {
                setSelectedGrantType(grant.id);
                setResult(null);
                setError('');
              }}
              disabled={loading}
              className="rounded-lg px-3 py-3 text-left transition-all focus:outline-none focus:ring-2 focus:ring-[#0a72ef] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                boxShadow: selectedGrantType === grant.id
                  ? 'rgba(0,0,0,0.12) 0px 0px 0px 2px'
                  : 'rgba(0,0,0,0.08) 0px 0px 0px 1px',
                background: selectedGrantType === grant.id ? '#171717' : '#fff',
              }}
            >
              <p className={`text-sm font-semibold leading-snug ${selectedGrantType === grant.id ? 'text-white' : 'text-[#171717]'}`}>
                {grant.label}
              </p>
              <p className={`text-xs mt-0.5 leading-tight ${selectedGrantType === grant.id ? 'text-white/70' : 'text-[#808080]'}`}>
                {grant.description}
              </p>
            </button>
          ))}
        </div>

        <button
          onClick={handleReframe}
          disabled={loading}
          className="w-full px-4 py-2.5 bg-[#171717] text-white rounded-md font-medium text-sm
                     hover:bg-[#2e2e2e] disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Spinner />
              Rewriting for {selectedGrant?.shortLabel} format...
            </>
          ) : (
            <>Reframe for {selectedGrant?.label}</>
          )}
        </button>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">{error}</p>
        )}
      </div>

      {result && (
        <div className="space-y-4">
          {result.mock_mode && (
            <div className="bg-amber-50 rounded-md px-4 py-2 text-xs text-amber-700 font-medium" style={{ boxShadow: 'rgba(251,191,36,0.3) 0px 0px 0px 1px' }}>
              MOCK MODE — Add your ANTHROPIC_API_KEY to backend/.env for real AI grant rewriting
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-[#171717]" style={{ letterSpacing: '-0.32px' }}>Reframed Protocol</h3>
              {selectedGrant && (
                <span
                  className="text-xs font-medium px-2.5 py-0.5 rounded-full text-[#4d4d4d]"
                  style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
                >
                  {selectedGrant.label}
                </span>
              )}
            </div>
            <button
              onClick={handleCopyAll}
              className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors text-[#4d4d4d] hover:text-[#171717]"
              style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
            >
              {copied ? 'Copied!' : 'Copy All'}
            </button>
          </div>

          <div className="space-y-2">
            {(result.sections || []).map((section, idx) => (
              <CollapsibleSection key={section.title || idx} section={section} index={idx} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
